import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19 } from "styled-components";
import React from "react";
import { hasItems, takeFirst } from "~/features/app/utils";
import { type Mode } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { Headline } from "~/features/ui/headline";
import { Button } from "~/features/ui/button";
import { isLinkValid } from "~/features/ui/link/utils";
import { Eyebrow } from "~/features/ui/eyebrow";
import { Link } from "~/features/ui/link";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
type PlainLink = Record<string, unknown> & {
  href?: string;
  newWindow?: boolean;
  text?: React.ReactNode;
};
type PlainHighlight = {
  left?: string | null;
  right?: string | null;
};
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
function getPlainLinkIcon(link?: PlainLink | null) {
  if (!link) {
    return undefined;
  }
  return link.newWindow ? ArrowTopRight : ArrowRight;
}
export type MediaHighlightsBlockViewProps = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  eyebrow?: string | null;
  hasSolidBackground?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  highlights?: PlainHighlight[] | null;
  isHighlighted?: boolean | null;
  link?: PlainLink | Array<PlainLink | null | undefined> | null;
  mediaDesktop?: React.ReactNode;
  mediaMobile?: React.ReactNode;
  mode?: string | null;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  theme?: string | null;
  title?: string | null;
};
export function MediaHighlightsBlockView({
  anchor,
  eyebrow,
  hasSolidBackground = false,
  hasTopDivider = true,
  headline,
  highlights,
  isHighlighted = false,
  link,
  mediaDesktop,
  mediaMobile,
  mode = "light",
  sectionProps,
  theme = "white",
  title
}: MediaHighlightsBlockViewProps) {
  const cta = takeFirst(link);
  const icon = getPlainLinkIcon(cta);
  // Every text block in this section is an overlay on top of the media. With no
  // media the media box has no height, so absolutely positioned overlays escape
  // the section and land on top of the previous one. Fall back to normal flow
  // (and theme-aware text) so authored copy stays inside its own section.
  const hasMedia = Boolean(mediaDesktop || mediaMobile);
  const overlayText = hasMedia ? {
    "--tw-text-opacity": "1",
    "color": "rgb(255 255 255 / var(--tw-text-opacity))"
  } : {
    "color": "var(--text-primary)"
  };
  return <_StyledSection {...sectionProps} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} $_css={[hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, hasSolidBackground && {
    "position": "relative",
    "backgroundColor": "var(--bg-primary)",
    "color": "var(--text-primary)"
  },
  // With media the section is transparent because the media carries the
  // contrast for the overlays. Stacked copy has nothing behind it, so the
  // section has to own a surface or `text-primary` is invisible.
  !hasMedia && {
    "backgroundColor": "var(--bg-primary)",
    "color": "var(--text-primary)"
  }, theme === "grey" && {
    "backgroundColor": "var(--bg-tertiary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        <_StyledDiv>
          <_StyledDiv2 $_css2={[{
          "position": "relative"
        }, !hasMedia && {
          "display": "flex",
          "flexDirection": "column",
          "gap": "var(--spacing-lg)"
        }]}>
            {hasMedia && <>
                <_StyledDiv3>{mediaDesktop}</_StyledDiv3>
                <_StyledDiv4>{mediaMobile}</_StyledDiv4>
              </>}

            {headline && <_StyledDiv5 $_css3={[{
            "zIndex": "20",
            "display": "flex",
            "width": "100%",
            "justifyContent": "center",
            "paddingLeft": "var(--spacing-xl)",
            "paddingRight": "var(--spacing-xl)"
          }, hasMedia ? {
            "position": "absolute",
            "left": "0",
            "top": "50%",
            "--tw-translate-y": "-50%",
            "transform": "translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y))"
          } : {
            "position": "relative"
          }]}>
                <_StyledHeadline type="h2" text={headline} $_css4={[{
              "maxWidth": "120rem",
              "textAlign": "center",
              "fontSize": "2.4rem",
              "lineHeight": "1.2",
              "fontWeight": "400",
              "@media (min-width: 1024px)": {
                "fontSize": "4.4rem",
                "lineHeight": "1.3",
                "letterSpacing": "0.044rem",
                "fontWeight": "400"
              }
            }, overlayText]} />
              </_StyledDiv5>}

            {(eyebrow || title || isLinkValid(cta)) && <_StyledDiv6 $_css5={[{
            "zIndex": "20",
            "display": "flex",
            "width": "100%",
            "flexDirection": "column",
            "alignItems": "flex-start",
            "justifyContent": "space-between",
            "gap": "var(--spacing-lg)",
            "padding": "var(--spacing-md)",
            "@media (min-width: 1024px)": {
              "flexDirection": "row",
              "alignItems": "flex-end",
              "padding": "var(--spacing-xl)"
            }
          }, hasMedia ? {
            "position": "absolute",
            "bottom": "0",
            "left": "0"
          } : {
            "position": "relative",
            "padding": "0",
            "@media (min-width: 1024px)": {
              "padding": "0"
            }
          }, overlayText]}>
                <_StyledDiv7>
                  {eyebrow && <_StyledEyebrow text={eyebrow} mode={mode as Mode} />}
                  {title && <_StyledHeadline2 type="h2" text={title} />}
                </_StyledDiv7>
                <div>{isLinkValid(cta) && <Button as={Link} {...cta} icon={icon} variant="underline" />}</div>
              </_StyledDiv6>}

            {hasItems(highlights) && highlights.length === 1 && <_StyledDiv8 $_css6={[{
            "zIndex": "20",
            "display": "flex",
            "flexDirection": "column",
            "columnGap": "var(--spacing-xl)",
            "rowGap": "var(--spacing-sm)"
          }, hasMedia ? {
            "position": "absolute",
            "bottom": "var(--spacing-md)",
            "left": "var(--spacing-md)",
            "@media (min-width: 1024px)": {
              "bottom": "var(--spacing-xl)",
              "left": "var(--spacing-xl)",
              "flexDirection": "row"
            }
          } : {
            "position": "relative",
            "@media (min-width: 1024px)": {
              "flexDirection": "row"
            }
          }, overlayText]}>
                {highlights[0]?.left && <_StyledH>{highlights[0].left}</_StyledH>}
                {highlights[0]?.right && <_StyledP $_css7={[hasMedia && {
              "maxWidth": "175px"
            }]}>{highlights[0].right}</_StyledP>}
              </_StyledDiv8>}
            {hasItems(highlights) && highlights.length > 1 && <_StyledDl $_css8={[{
            "zIndex": "20",
            "display": "flex",
            "flexDirection": "column",
            "rowGap": "var(--spacing-sm)",
            "borderRadius": "4px",
            "paddingLeft": "var(--spacing-lg)",
            "paddingRight": "var(--spacing-lg)",
            "paddingTop": "var(--spacing-md)",
            "paddingBottom": "var(--spacing-md)",
            "@media (min-width: 1024px)": {
              "rowGap": "var(--spacing-lg)",
              "paddingLeft": "var(--spacing-xl)",
              "paddingRight": "var(--spacing-xl)",
              "paddingTop": "var(--spacing-xl)",
              "paddingBottom": "var(--spacing-xl)"
            }
          }, hasMedia ? {
            "position": "absolute",
            "bottom": "0",
            "left": "0",
            "backgroundColor": "rgba(0, 7, 4, 0.4)",
            "--tw-text-opacity": "1",
            "color": "rgb(255 255 255 / var(--tw-text-opacity))",
            "--tw-backdrop-blur": "blur(4px)",
            "backdropFilter": "var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia)"
          } : {
            "position": "relative",
            "padding": "0",
            "color": "var(--text-primary)",
            "@media (min-width: 1024px)": {
              "padding": "0"
            }
          }]}>
                {highlights.map((highlight, index) => <_StyledDiv9 key={index}>
                    {highlight.left && <_StyledDd $_css9={[{
                "width": "5rem",
                "fontSize": "1.4rem",
                "lineHeight": "1.5",
                "letterSpacing": "0.014000000000000002rem",
                "fontWeight": "500"
              }, overlayText]}>{highlight.left}</_StyledDd>}
                    {highlight.right && <_StyledDt $_css10={[{
                "width": "100%",
                "textAlign": "left",
                "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
                "fontSize": "1.4rem",
                "lineHeight": "1.1",
                "letterSpacing": "0.028000000000000004rem",
                "fontWeight": "400"
              }, hasMedia ? {
                "--tw-text-opacity": "1",
                "color": "rgb(208 208 218 / var(--tw-text-opacity))"
              } : {
                "color": "var(--text-secondary)"
              }]}>
                        {highlight.right}
                      </_StyledDt>}
                  </_StyledDiv9>)}
              </_StyledDl>}
          </_StyledDiv2>
        </_StyledDiv>
      </_StyledWrap>
    </_StyledSection>;
}
var _StyledSection = /*#__PURE__*/_styled("section").withConfig({
  componentId: "sc-sf-ogjsvw-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-ogjsvw-1"
})({
  "borderWidth": "0px"
});
var _StyledDiv = _styled3("div").withConfig({
  componentId: "sc-sf-ogjsvw-2"
})({
  "position": "relative",
  "padding": "var(--spacing-md)",
  "@media (min-width: 1024px)": {
    "padding": "var(--spacing-xl)"
  }
});
var _StyledDiv2 = _styled4("div").withConfig({
  componentId: "sc-sf-ogjsvw-3"
})`${p => p.$_css2}`;
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-ogjsvw-4"
})({
  "display": "none",
  "@media (min-width: 1024px)": {
    "display": "block"
  }
});
var _StyledDiv4 = _styled6("div").withConfig({
  componentId: "sc-sf-ogjsvw-5"
})({
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledDiv5 = _styled7("div").withConfig({
  componentId: "sc-sf-ogjsvw-6"
})`${p => p.$_css3}`;
var _StyledHeadline = _styled8(Headline).withConfig({
  componentId: "sc-sf-ogjsvw-7"
})`${p => p.$_css4}`;
var _StyledDiv6 = _styled9("div").withConfig({
  componentId: "sc-sf-ogjsvw-8"
})`${p => p.$_css5}`;
var _StyledDiv7 = _styled10("div").withConfig({
  componentId: "sc-sf-ogjsvw-9"
})({
  "display": "flex",
  "maxWidth": "37rem",
  "flexDirection": "column",
  "gap": "var(--spacing-xs)"
});
var _StyledEyebrow = _styled11(Eyebrow).withConfig({
  componentId: "sc-sf-ogjsvw-10"
})({
  "fontFamily": "Saans, ui-sans-serif, system-ui, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"",
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledHeadline2 = _styled12(Headline).withConfig({
  componentId: "sc-sf-ogjsvw-11"
})({
  "fontSize": "1.6rem",
  "lineHeight": "1.5",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.8rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.018rem",
    "fontWeight": "400"
  }
});
var _StyledDiv8 = _styled13("div").withConfig({
  componentId: "sc-sf-ogjsvw-12"
})`${p => p.$_css6}`;
var _StyledH = _styled14("h3").withConfig({
  componentId: "sc-sf-ogjsvw-13"
})({
  "fontSize": "2rem",
  "lineHeight": "1.3",
  "letterSpacing": "0.02rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "2.4rem",
    "lineHeight": "1.3",
    "letterSpacing": "0.024rem",
    "fontWeight": "400"
  }
});
var _StyledP = _styled15("p").withConfig({
  componentId: "sc-sf-ogjsvw-14"
})`${p => p.$_css7}`;
var _StyledDl = _styled16("dl").withConfig({
  componentId: "sc-sf-ogjsvw-15"
})`${p => p.$_css8}`;
var _StyledDiv9 = _styled17("div").withConfig({
  componentId: "sc-sf-ogjsvw-16"
})({
  "display": "flex",
  "gap": "var(--spacing-lg)",
  "verticalAlign": "baseline"
});
var _StyledDd = _styled18("dd").withConfig({
  componentId: "sc-sf-ogjsvw-17"
})`${p => p.$_css9}`;
var _StyledDt = /*#__PURE__*/_styled19("dt").withConfig({
  displayName: "media-highlights___StyledDt",
  componentId: "sc-1jnecpx-0"
})(["", ""], p => p.$_css10);